/**
 * Chummys motion layer (global)
 *  1. Cursor dot — trails the pointer with a little lag, grows over links and
 *     "magic" targets (logo, hero switcher, hero button).
 *  2. Homepage intro — once per session: the hero zooms up from a tiny card while
 *     the logo slides in, holds, fades; then the rest of the page reveals.
 *  3. Header logo mark — if the homepage has no hero, drop the mark class so the
 *     normal wordmark shows (the swap itself is pure CSS driven by chummys-hero.js).
 */
(() => {
  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  const SESSION_KEY = 'chummysIntroPlayed';

  const markPlayed = () => { try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* private mode */ } };

  /* ------------------------------------------------------------ */
  /* 1. Cursor dot                                                 */
  /* ------------------------------------------------------------ */
  const initCursor = () => {
    const cursor = document.querySelector('.chummys-cursor');
    if (!cursor || isTouch || reduced) return;
    if (!window.matchMedia('(min-width: 981px) and (hover: hover)').matches) return;

    const MAGIC = '[data-cursor="magic"], .header__logo a, .chummys-hero__tab, .chummys-hero__cta';
    const LINK = 'a, button, [role="button"], input, select, textarea, label, summary, .product-card, [data-cursor="link"]';

    let targetX = -100, targetY = -100, x = -100, y = -100;
    let raf = null;
    let visible = false;

    const tick = () => {
      x += (targetX - x) / 6;
      y += (targetY - y) / 6;
      cursor.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;
      if (Math.abs(targetX - x) > 0.1 || Math.abs(targetY - y) > 0.1) raf = requestAnimationFrame(tick);
      else raf = null;
    };
    const kick = () => { if (raf === null && !document.hidden) raf = requestAnimationFrame(tick); };

    // Hover state is re-evaluated on every move (not just mouseover) so it survives
    // the DOM changing underneath a still pointer, e.g. after a switcher click.
    const setState = (target) => {
      if (!(target instanceof Element)) return;
      const magic = target.closest(MAGIC);
      const link = !magic && target.closest(LINK);
      cursor.classList.toggle('is-magic', !!magic);
      cursor.classList.toggle('is-link', !!link);
    };

    document.addEventListener('mousemove', (e) => {
      targetX = e.clientX;
      targetY = e.clientY;
      if (!visible) {
        visible = true;
        x = targetX; y = targetY;
        cursor.classList.add('is-visible');
      }
      setState(e.target);
      kick();
    }, { passive: true });

    document.addEventListener('mouseover', (e) => setState(e.target), { passive: true });

    // pointer left the window
    document.documentElement.addEventListener('mouseleave', () => {
      visible = false;
      cursor.classList.remove('is-visible', 'is-link', 'is-magic', 'is-down');
    });

    document.addEventListener('mousedown', () => cursor.classList.add('is-down'), { passive: true });
    document.addEventListener('mouseup', () => cursor.classList.remove('is-down'), { passive: true });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) kick(); });
  };

  /* ------------------------------------------------------------ */
  /* 2. Homepage intro                                             */
  /* ------------------------------------------------------------ */
  const initIntro = () => {
    if (!root.classList.contains('chummys-intro')) return;

    const intro = document.querySelector('.chummys-intro-logo');
    const logo = intro && intro.querySelector('.chummys-intro-logo__img');
    const frame = document.querySelector('.chummys-hero__frame');
    const heroImg = frame && frame.querySelector('.chummys-hero__panel.is-active img');

    const priorScrollRestoration = ('scrollRestoration' in history) ? history.scrollRestoration : null;
    const restoreScrollRestoration = () => {
      if (priorScrollRestoration) { try { history.scrollRestoration = priorScrollRestoration; } catch (e) { /* ignore */ } }
    };

    const finishInstantly = () => {
      restoreScrollRestoration();
      root.classList.remove('chummys-intro');
      if (intro) intro.remove();
      markPlayed();
      document.dispatchEvent(new CustomEvent('chummys:intro:done'));
    };

    if (!intro || !logo || !frame || reduced || typeof frame.animate !== 'function') {
      finishInstantly();
      return;
    }

    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    if ('scrollRestoration' in history) {
      try { history.scrollRestoration = 'manual'; } catch (e) { /* ignore */ }
    }

    const run = () => {
      window.scrollTo(0, 0);
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      const s0 = mobile ? 0.5 : 0.2;
      const s1 = mobile ? 0.7 : 0.32;
      const logoY = mobile ? 80 : 160;
      const IN_OUT = 'cubic-bezier(0.45, 0, 0.55, 1)';
      const OUT2 = 'cubic-bezier(0.2, 1, 0.3, 1)';
      const OUT4 = 'cubic-bezier(0.16, 1, 0.3, 1)';
      // fill: 'forwards' only — a 'backwards'/'both' fill would apply a delayed animation's
      // first keyframe immediately and override the animation running before it.
      const o = (duration, delay, easing) => ({ duration, delay, easing, fill: 'forwards' });

      const anims = [];
      // hero card pops in small
      anims.push(frame.animate(
        [{ transform: `scale(${s0})`, opacity: 0 }, { transform: `scale(${s1})`, opacity: 1 }],
        o(1200, 0, IN_OUT)
      ));
      // logo wrapper rises
      anims.push(intro.animate(
        [{ opacity: 0, transform: 'translateY(50px) scale(0.9)' }, { opacity: 1, transform: 'translateY(0) scale(1)' }],
        o(800, 400, OUT2)
      ));
      // logo slides up with a skew
      anims.push(logo.animate(
        [{ opacity: 0, transform: `translateY(${logoY}px) skewY(15deg)` }, { opacity: 1, transform: 'translateY(0) skewY(0deg)' }],
        o(800, 600, OUT2)
      ));
      // hold, then the logo fades and shrinks away
      anims.push(logo.animate(
        [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.85)' }],
        o(700, 1700, OUT4)
      ));
      anims.push(intro.animate(
        [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.9)' }],
        o(600, 2100, IN_OUT)
      ));
      // hero grows to full size
      const last = frame.animate(
        [{ transform: `scale(${s1})`, opacity: 1 }, { transform: 'scale(1)', opacity: 1 }],
        o(1200, 2000, OUT4)
      );
      anims.push(last);

      const done = () => {
        // commit the end state to inline style, THEN drop the animations, so the
        // frame cannot snap back between cancel() and the next paint
        frame.style.transform = 'none';
        frame.style.opacity = '1';
        anims.forEach((a) => { try { a.cancel(); } catch (e) { /* ignore */ } });
        intro.remove();
        restoreScrollRestoration();
        root.classList.remove('chummys-intro', 'chummys-revealing');
        // ...and hand the frame back to the stylesheet in the same task. Left
        // in place, those two inline declarations out-rank the scroll-driven
        // scale/opacity in chummys-hero.css for the rest of the pageview, so
        // the hero never shrank under the next section on any visit where the
        // intro had played. --ch-progress is 0 here, so scale(1) is what the
        // stylesheet resolves to anyway: nothing moves.
        frame.style.removeProperty('transform');
        frame.style.removeProperty('opacity');
        markPlayed();
        document.dispatchEvent(new CustomEvent('chummys:intro:done'));
      };

      // reveal header + the rest of the page while the hero is still growing
      window.setTimeout(() => {
        root.classList.add('chummys-revealing');
        root.classList.remove('chummys-intro');
      }, 2450);

      last.finished.then(done).catch(done);
    };

    // Only the hero image is worth waiting for. Waiting on the theme's
    // `page:loaded` put ~370KB of deferred script on the critical path for the
    // hero paint, which is the page's LCP element; fonts get a short deadline
    // rather than an open-ended one.
    const deadline = (p, ms) => Promise.race([p, new Promise((r) => window.setTimeout(r, ms))]);
    const fonts = document.fonts && document.fonts.ready
      ? deadline(document.fonts.ready.catch(() => {}), 400)
      : Promise.resolve();
    const image = heroImg && !heroImg.complete && heroImg.decode
      ? heroImg.decode().catch(() => {})
      : Promise.resolve();

    deadline(Promise.all([fonts, image]), 1200)
      .then(() => requestAnimationFrame(() => requestAnimationFrame(run)));
  };

  /* ------------------------------------------------------------ */
  /* 3. Header mark guard                                          */
  /* ------------------------------------------------------------ */
  const initMark = () => {
    if (root.classList.contains('chummys-has-hero') && !document.querySelector('chummys-hero')) {
      root.classList.remove('chummys-has-hero', 'chummys-scrolled');
    }
  };

  const boot = () => {
    initMark();
    initCursor();
    initIntro();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  // When the theme editor re-renders, make sure the mark guard still holds.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initMark);
    document.addEventListener('shopify:section:unload', () => window.setTimeout(initMark, 50));
  }
})();
